import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api.js';
import { useSession, useTheme } from '../session.js';
import { Segmented, Tile } from '../controls.js';
import { AvatarChooser } from '../avatar.js';
import { useToast } from '../ui.js';
import {
  CheckIcon, GraduationIcon, MoonIcon, PlaneIcon, ShieldIcon, SunIcon,
} from '../icons.js';

type Mode = 'school' | 'join';

export function Signup(): JSX.Element {
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>(params.get('code') ? 'join' : 'school');
  const [theme, toggleTheme] = useTheme();

  return (
    <div className="auth">
      <header className="topbar">
        <Link to="/" className="topbar__brand" style={{ textDecoration: 'none' }}>
          <span className="topbar__mark" />
          FlightSlots
        </Link>
        <div className="topbar__right">
          <button className="chip chip--icon" onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light appearance' : 'Switch to dark appearance'}>
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>
          <Link to="/login" className="chip" style={{ textDecoration: 'none' }}>Sign in</Link>
        </div>
      </header>

      <div className="auth__body">
        <div className="auth__intro">
          <h1 className="auth__title">Get started</h1>
          <p className="auth__lede">
            Set up a school and you own the fleet, the roster and the rates. Join one you
            already fly with and you get your schedule, your syllabus and the school's documents.
          </p>
        </div>

        <div style={{ maxWidth: 560, width: '100%' }}>
          <Segmented
            grow
            value={mode}
            onChange={setMode}
            label="What are you signing up for?"
            options={[
              { value: 'school', label: 'Set up a school' },
              { value: 'join', label: 'Join a school' },
            ]}
          />
          <div style={{ marginTop: 16 }}>
            {mode === 'school' ? <CreateSchool /> : <JoinSchool initialCode={params.get('code') ?? ''} />}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Create a school ──────────────────────────────────────────────────── */

const ORG_TYPES = [
  { value: 'freelance', title: 'Freelance CFI', body: 'One instructor, one hat. No dispatch desk, no stage gates — you own the block, the invoice and the record.' },
  { value: 'part61', title: 'Part 61 school', body: 'Lessons follow a plan, not a filed syllabus. Dispatch owns the aircraft; instructors own the training.' },
  { value: 'part141', title: 'Part 141 school', body: 'Stage checks gate the next lesson and the chief instructor approves stage records.' },
] as const;

function CreateSchool(): JSX.Element {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const toast = useToast();

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);

  const [name, setName] = useState('');
  const [icao, setIcao] = useState('');
  const [type, setType] = useState<'freelance' | 'part61' | 'part141'>('part61');
  const [ownerName, setOwnerName] = useState('');
  const [ratings, setRatings] = useState('CFI');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [addAircraft, setAddAircraft] = useState(false);
  const [tail, setTail] = useState('');
  const [model, setModel] = useState('');
  const [hobbs, setHobbs] = useState('');
  const [rate, setRate] = useState('170');

  /** What each step is still waiting for, so a dead button explains itself. */
  const stepIssues: string[][] = [
    [
      name.trim().length < 2 ? 'the school name' : '',
      !/^[A-Za-z0-9]{3,6}$/.test(icao.trim()) ? 'the home airport identifier, 3 to 6 letters or numbers' : '',
    ].filter(Boolean),
    [
      ownerName.trim().length < 2 ? 'your name' : '',
      !/\S+@\S+\.\S+/.test(email) ? 'a valid email address' : '',
      pw.length < 10 ? `a password of at least 10 characters${pw.length ? ` (${10 - pw.length} more)` : ''}` : '',
    ].filter(Boolean),
    addAircraft
      ? [
          tail.trim().length < 2 ? 'the aircraft registration' : '',
          model.trim().length < 1 ? 'the aircraft type' : '',
        ].filter(Boolean)
      : [],
  ];
  const stepValid = stepIssues.map((issues) => issues.length === 0);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setFieldErrors([]);
    try {
      const result = await api.post<{ joinCode: string; userId?: number }>('/signup/school', {
        school: { name: name.trim(), icao: icao.trim(), type },
        owner: { name: ownerName.trim(), email: email.trim(), password: pw, ratings: ratings.trim() || undefined },
        aircraft: addAircraft
          ? {
              tail: tail.trim(), model: model.trim(),
              hobbs: hobbs ? Number(hobbs) : 0,
              rateCents: Math.round((Number(rate) || 0) * 100),
              ifrCapable: true, nightCapable: true,
            }
          : null,
      });
      await uploadPhoto(photo);
      await refresh();
      toast.ok(`${name.trim()} is set up`, `Your join code is ${result.joinCode}.`);
      navigate('/schedule', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.details ?? []);
      } else {
        setError('Something went wrong. Try again.');
      }
      setBusy(false);
    }
  };

  return (
    <div className="card auth__card">
      <div className="auth__steps">
        {['School', 'You', 'First aircraft'].map((label, i) => (
          <div key={label} className={`auth__step${i === step ? ' auth__step--on' : ''}${i < step ? ' auth__step--done' : ''}`}>
            <span className="auth__step-n">{i < step ? <CheckIcon style={{ width: 13, height: 13 }} /> : i + 1}</span>
            {label}
          </div>
        ))}
      </div>

      <div className="card__body stack">
        {step === 0 && (
          <>
            <label className="field">
              <span>School name</span>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Meridian Flight Center" autoFocus />
            </label>
            <label className="field">
              <span>Home airport</span>
              <input className="input" value={icao} onChange={(e) => setIcao(e.target.value.toUpperCase())}
                placeholder="KMRB" maxLength={6} autoCapitalize="characters" spellCheck={false} />
              <span className="small muted" style={{ display: 'block', marginTop: 5 }}>
                The identifier you file under — 3 to 6 letters or numbers, such as KMRB or EGLL.
                Used for sunrise, sunset and the field header.
              </span>
            </label>
            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="field" style={{ padding: 0 }}><span>How you operate</span></legend>
              <div className="stack" style={{ gap: 8 }}>
                {ORG_TYPES.map((o) => (
                  <button key={o.value} type="button" className={`choice${type === o.value ? ' choice--on' : ''}`}
                    onClick={() => setType(o.value)} aria-pressed={type === o.value}>
                    <span className="choice__radio" aria-hidden="true">
                      {type === o.value && <CheckIcon style={{ width: 12, height: 12 }} />}
                    </span>
                    <span>
                      <span className="choice__title">{o.title}</span>
                      <span className="choice__body">{o.body}</span>
                    </span>
                  </button>
                ))}
              </div>
            </fieldset>
          </>
        )}

        {step === 1 && (
          <>
            <label className="field">
              <span>Your name</span>
              <input className="input" value={ownerName} onChange={(e) => setOwnerName(e.target.value)}
                placeholder="Dana Whitlock" autoComplete="name" autoFocus />
            </label>
            <label className="field">
              <span>Your ratings</span>
              <input className="input" value={ratings} onChange={(e) => setRatings(e.target.value)}
                placeholder="CFI · CFII · MEI" />
            </label>
            <label className="field">
              <span>Email</span>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" autoComplete="username" spellCheck={false} />
            </label>
            <label className="field">
              <span>Password</span>
              <input className="input" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                autoComplete="new-password" />
              <span className="small muted" style={{ display: 'block', marginTop: 5 }}>
                At least 10 characters. {pw.length > 0 && pw.length < 10 && `${10 - pw.length} to go.`}
              </span>
            </label>
            <label className="field">
              <span>Profile picture</span>
              <AvatarChooser value={photo} onChange={setPhoto} initials={initialsFrom(ownerName)} label="Your students" />
            </label>
            <p className="small muted" style={{ margin: 0, textWrap: 'pretty' }}>
              You are set up as chief instructor: full access to the fleet, the roster, training
              records and rates, and you can be booked to instruct.
            </p>
          </>
        )}

        {step === 2 && (
          <>
            <label className="choice" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={addAircraft} onChange={(e) => setAddAircraft(e.target.checked)}
                 />
              <span>
                <span className="choice__title">Add an aircraft now</span>
                <span className="choice__body">You can add the rest of the fleet later from the Aircraft screen.</span>
              </span>
            </label>

            {addAircraft && (
              <>
                <div className="auth__pair">
                  <label className="field">
                    <span>Registration</span>
                    <input className="input" value={tail} onChange={(e) => setTail(e.target.value.toUpperCase())}
                      placeholder="N7215F" spellCheck={false} />
                  </label>
                  <label className="field">
                    <span>Type</span>
                    <input className="input" value={model} onChange={(e) => setModel(e.target.value)}
                      placeholder="C172S" />
                  </label>
                </div>
                <div className="auth__pair">
                  <label className="field">
                    <span>Hobbs</span>
                    <input className="input" inputMode="decimal" value={hobbs}
                      onChange={(e) => setHobbs(e.target.value)} placeholder="0.0" />
                  </label>
                  <label className="field">
                    <span>Rate per hour, wet</span>
                    <input className="input" inputMode="decimal" value={rate}
                      onChange={(e) => setRate(e.target.value)} placeholder="170" />
                  </label>
                </div>
                <p className="small muted" style={{ margin: 0, textWrap: 'pretty' }}>
                  The next 100-hour inspection is set 100 hours ahead of the Hobbs you enter.
                  Correct it from the aircraft record if that is not right.
                </p>
              </>
            )}
          </>
        )}

        {error && <AuthError message={error} details={fieldErrors} />}
      </div>

      <div className="auth__foot">
        {step > 0 && <button className="btn" onClick={() => setStep((s) => s - 1)} disabled={busy}>Back</button>}
        <span className="small muted" style={{ flex: 1, minWidth: 0, textWrap: 'pretty' }}>
          {stepIssues[step].length > 0 && `Still needs ${listOut(stepIssues[step])}.`}
        </span>
        {step < 2 ? (
          <button className="btn btn--primary" disabled={!stepValid[step]} onClick={() => setStep((s) => s + 1)}>
            Continue
          </button>
        ) : (
          <button className="btn btn--primary" disabled={busy || !stepValid[2]} onClick={submit}>
            {busy ? 'Setting up…' : 'Create school'}
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Join a school ────────────────────────────────────────────────────── */

interface SchoolPreview {
  name: string; icao: string; type: string;
  /** Which code was entered decides what the account becomes. */
  joining: 'student' | 'instructor';
  courses: { id: number; name: string }[];
}

function JoinSchool({ initialCode }: { initialCode: string }): JSX.Element {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const toast = useToast();

  const [code, setCode] = useState(initialCode);
  const [school, setSchool] = useState<SchoolPreview | null>(null);
  const [courseId, setCourseId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [ratings, setRatings] = useState('CFI');
  const [certificate, setCertificate] = useState('');
  const [cfiExpires, setCfiExpires] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<string[]>([]);

  const lookup = async () => {
    setBusy(true);
    setError(null);
    try {
      const found = await api.get<SchoolPreview>(`/signup/school/${encodeURIComponent(code.trim())}`);
      setSchool(found);
      setCourseId(found.courses[0]?.id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not find that school');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    setDetails([]);
    try {
      const asInstructor = school?.joining === 'instructor';
      const result = await api.post<{ orgName: string }>('/signup/join', {
        code: code.trim(), name: name.trim(), email: email.trim(), password: pw,
        ...(asInstructor
          ? { ratings: ratings.trim(), certificateNumber: certificate.trim(), cfiExpiresOn: cfiExpires }
          : { courseId }),
      });
      await uploadPhoto(photo);
      await refresh();
      toast.ok(
        `Welcome to ${result.orgName}`,
        asInstructor ? 'Set your working hours from the instructor tab.' : 'Your instructor will be assigned shortly.',
      );
      navigate(asInstructor ? '/schedule' : '/portal', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) { setError(err.message); setDetails(err.details ?? []); }
      else setError('Something went wrong. Try again.');
      setBusy(false);
    }
  };

  const asInstructor = school?.joining === 'instructor';
  const joinIssues = [
    name.trim().length < 2 ? 'your name' : '',
    !/\S+@\S+\.\S+/.test(email) ? 'a valid email address' : '',
    pw.length < 10 ? `a password of at least 10 characters${pw.length ? ` (${10 - pw.length} more)` : ''}` : '',
    asInstructor && certificate.trim().length < 4 ? 'your CFI certificate number' : '',
    asInstructor && !/^\d{4}-\d{2}-\d{2}$/.test(cfiExpires) ? 'the expiry on your CFI certificate' : '',
  ].filter(Boolean);
  const canSubmit = joinIssues.length === 0;

  if (!school) {
    return (
      <div className="card auth__card">
        <div className="card__body stack">
          <div className="row" style={{ gap: 12, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
            <Tile color="blue" size="lg"><GraduationIcon /></Tile>
            <span>
              <span style={{ display: 'block', fontWeight: 600 }}>Enter your school's code</span>
              <span className="small muted" style={{ display: 'block', textWrap: 'pretty' }}>
                Eight characters, from your instructor or the front desk. The code decides whether
                you join as a student or as an instructor. Case and spacing do not matter.
              </span>
            </span>
          </div>
          <label className="field">
            <span>Join code</span>
            <input className="input auth__code" value={code} placeholder="MRB2-FLY7"
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && code.trim().length >= 4) lookup(); }}
              autoCapitalize="characters" spellCheck={false} autoFocus />
          </label>
          {error && <AuthError message={error} details={[]} />}
        </div>
        <div className="auth__foot">
          <span style={{ flex: 1 }} />
          <button className="btn btn--primary" disabled={busy || code.trim().length < 4} onClick={lookup}>
            {busy ? 'Looking up…' : 'Find school'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card auth__card">
      <div className="card__body stack">
        <div className="row" style={{ gap: 12, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
          <Tile color="green" size="lg"><CheckIcon /></Tile>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontWeight: 600, fontSize: 15 }}>{school.name}</span>
            <span className="small muted">
              {school.icao} · {school.type === 'part141' ? 'Part 141 school' : school.type === 'part61' ? 'Part 61 school' : 'Freelance CFI'}
            </span>
          </span>
          <button className="btn btn--link" onClick={() => { setSchool(null); setError(null); }}>Change</button>
        </div>

        {asInstructor && (
          <div style={{
            padding: '11px 13px', borderRadius: 'var(--r-md)',
            background: 'var(--tint-indigo, var(--tint-blue))', fontSize: 12.5, textWrap: 'pretty',
          }}>
            This is an instructor code, so you are joining {school.name} as a CFI — your own
            students, your own logbook, and your name on the endorsements you give.
          </div>
        )}

        {!asInstructor && school.courses.length > 1 && (
          <label className="field">
            <span>Course</span>
            <select className="select" value={courseId ?? ''} onChange={(e) => setCourseId(Number(e.target.value))}>
              {school.courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}

        <label className="field">
          <span>Your name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Ellie Chan" autoComplete="name" autoFocus />
        </label>
        <label className="field">
          <span>Email</span>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" autoComplete="username" spellCheck={false} />
        </label>
        <label className="field">
          <span>Password</span>
          <input className="input" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
            autoComplete="new-password" />
          <span className="small muted" style={{ display: 'block', marginTop: 5 }}>
            At least 10 characters. {pw.length > 0 && pw.length < 10 && `${10 - pw.length} to go.`}
          </span>
        </label>

        {asInstructor && (
          <>
            <label className="field">
              <span>Ratings</span>
              <input className="input" value={ratings} onChange={(e) => setRatings(e.target.value)}
                placeholder="CFI · CFII · MEI" spellCheck={false} />
            </label>
            <label className="field">
              <span>CFI certificate number</span>
              <input className="input" value={certificate} spellCheck={false}
                onChange={(e) => setCertificate(e.target.value.toUpperCase())}
                placeholder="4102284CFI" />
            </label>
            <label className="field">
              <span>Certificate expires</span>
              <input className="input" type="date" value={cfiExpires}
                onChange={(e) => setCfiExpires(e.target.value)} />
              <span className="small muted" style={{ display: 'block', marginTop: 5 }}>
                Printed on every logbook entry you certify, and checked before each lesson you are
                booked on.
              </span>
            </label>
          </>
        )}

        <label className="field">
          <span>Profile picture</span>
          <AvatarChooser value={photo} onChange={setPhoto} initials={initialsFrom(name)}
            label={asInstructor ? 'Your students' : 'Your instructor'} />
        </label>

        <p className="small muted" style={{ margin: 0, textWrap: 'pretty' }}>
          {asInstructor
            ? 'You join as an instructor: the schedule, your students and their training records. Fleet settings and school-wide billing stay with management.'
            : "You join as a student: your own lessons, requests, syllabus, the school's documents and your account balance."}
        </p>

        {error && <AuthError message={error} details={details} />}
      </div>
      <div className="auth__foot">
        <span className="small muted" style={{ flex: 1, minWidth: 0, textWrap: 'pretty' }}>
          {joinIssues.length > 0 && `Still needs ${listOut(joinIssues)}.`}
        </span>
        <button className="btn btn--primary" disabled={busy || !canSubmit} onClick={submit}>
          {busy ? 'Joining…' : `Join ${school.name}${asInstructor ? ' as a CFI' : ''}`}
        </button>
      </div>
    </div>
  );
}

/**
 * The account exists by the time this runs, and sign-up signs you straight in,
 * so the picture is attached to whoever the session now says you are. A failed
 * upload is not worth failing the sign-up over — it can be set from the account
 * menu afterwards.
 */
async function uploadPhoto(dataUrl: string | null): Promise<void> {
  if (!dataUrl) return;
  try {
    const session = await api.get<{ user: { id: number } | null }>('/session');
    if (session.user) await api.put(`/users/${session.user.id}/avatar`, { dataUrl });
  } catch {
    // Left for the account menu.
  }
}

/** "a, b and c" — so the hint reads as a sentence. */
function listOut(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function initialsFrom(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
}

function AuthError({ message, details }: { message: string; details: string[] }): JSX.Element {
  return (
    <div className="auth__error" role="alert">
      <ShieldIcon style={{ width: 17, height: 17, flex: 'none', marginTop: 1 }} />
      <span>
        <span style={{ display: 'block', fontWeight: 600 }}>{message}</span>
        {details.map((d) => <span key={d} style={{ display: 'block', marginTop: 2 }}>{d}</span>)}
      </span>
    </div>
  );
}

export { PlaneIcon };
